import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PageHeading from '../../Components/PageHeading';
import { useNavigate, useParams } from 'react-router-dom';
import CultService from '../../Services/CultService';
import { PATH_NAME } from '../../Routes/Pathname';
import toast from 'react-hot-toast';
import { HelperTexts } from '../../Utils/helper/HelperTexts';
import PageLoader from '../../Components/PageLoader';
import CampaignDetailsReports from './Components/CampaignDetailsReports';
import CampaignDetailsInsights from './Components/CampaignDetailsInsights';
import { useSelector } from 'react-redux';
import CampaignPreview from './Components/CampaignSteps/CampaignPreview';
import SearchBar from '../../Components/SearchBar';
import { AppColors } from '../../Utils/helper/AppColors';
import { getColorSettings } from '../../Utils/helper/commonUtils';
import ReportCallDetailsSummary from './ReportCallDetailsSummary';

export default function CampaignDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [details, setDetails] = useState({});
	const [loading, setLoading] = useState(false);
	const [value, setValue] = React.useState(0);
	const company = useSelector((state) => state?.app?.company);
	const [insights, setInsights] = useState([]);
	const [canShowReportSummary, setCanShowReportSummary] = useState({}); // just to delay the report summary rendering  <ReportCallDetailsSummary/>
	const [selectedType, setSelectedType] = useState('total'); // to handle the selected type for report summary like "shaduled" , "reshared", etc.
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [pagination, setPagination] = useState({ count: 0, rowsPerPage: 1, page: 1 });
	
	const handleChange = (event, newValue) => {
		if (newValue !== null) {
			setValue(newValue);
		}
	};

	const getDetails = async () => {
		setLoading(true);
		try {
			let res = await CultService.getCampaignById({ params: { id } });
			if (res.status == 200) {
				setDetails(res.data);
			}
		} catch (err) {
			toast.error(HelperTexts.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const getCampaignInsights = async () => {
		setLoading(true);
		try {
			let res = await CultService.getCampaignInsights({ params: { companyId: company?._id, campaigns: [id] } });
			setInsights(res.data.data);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const getCampaignFilteredUsers = async (page = 1, limit = pagination?.rowsPerPage ) => {
		setLoading(true);
		try {
			let res = await CultService.getCampaignFilteredUsers({ params: { compaignId: id , page, limit , callType: selectedType } });
			setFilteredUsers(res.data.data);
			setPagination({
					count: res.data.pagination?.total || 0,
					rowsPerPage: res.data.pagination?.limit || 10,
					totalPages: res.data.pagination?.totalPages || 1,
					page: res.data.pagination?.page || 1,
				});
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getDetails();
	}, []);

	useEffect(() => {
		getCampaignFilteredUsers(1,10);
	}, [selectedType]);

	useEffect(() => {
		if (company) {
			getCampaignInsights();
		}
	}, [company]);

	const tabs = [
		{
			code: 'Preview',
			Component: CampaignPreview,
			props: { details, viewOnly: true },
		},
		{
			code: 'Insights',
			Component: CampaignDetailsInsights,
			props: { insights },
		},
		{
			code: 'Reports',
			Component: CampaignDetailsReports,
			props: { filteredUsers, setCanShowReportSummary, selectedType , getList : getCampaignFilteredUsers , pagination },
		},
	];

	// function transformData(data) {
	// 	return data?.flatMap((cluster) =>
	// 		cluster?.alignmentIssues?.map((issue) => ({
	// 			clusterName: cluster.clusterName,
	// 			name: issue.name,
	// 			solution: issue.solution,
	// 			employeeReports: issue.employeeReports,
	// 			percentage: issue.percentage,
	// 			_id: issue._id,
	// 		})),
	// 	);
	// }

	// useEffect(() => {
	// 	setItems(transformData(details?.insights));
	// }, [details]);

	const [search, setSearch] = useState('');
	const localSettingColors = getColorSettings();

	return (
		<Stack sx={{ padding: 2 }}>
			<PageHeading
				onBackClick={() => navigate(PATH_NAME.CAMPAIGN_LIST)}
				title={details?.basicDetails?.name}
				infoTooltip={details?.basicDetails?.description}
				wrapContext={true}
				// subTitle={details?.basicDetails?.description}
				renderRight={
					value === 2 && (
						<SearchBar
							search={search}
							setSearch={setSearch}
							color={localSettingColors.fontColor}
							bgColor={localSettingColors.quinary}
							width={{ xs: '80%', md: 'auto' }}
						/>
					)
				}
			/>
			{value === 2 && canShowReportSummary && <ReportCallDetailsSummary data={{ total: details?.totalCalls, completed : details?.totalCallsComplete, pending: details?.totalCallsPending , rescheduled: details?.totalCallsRescheduled, scheduled: details?.totalCallsScheduled }} selectedType={selectedType} setSelectedType={setSelectedType} />}

			<Stack mt={2}>
				<Stack>
					<ToggleButtonGroup size="small" color="primary" sx={{ width: '100%' }} value={value} exclusive onChange={handleChange}>
						{tabs?.map((tab, index) => (
							<ToggleButton
								key={index}
								sx={(theme) => ({
									textTransform: 'none',
									borderRadius: '8px 8px 0px 0px',
									border: '1px solid',
									borderColor: theme.palette.primary.lightest_shade,
									alignItems: 'center',
									flex: 1,
									backgroundColor: '#F9F9F9',
									'&.Mui-selected': {
										backgroundColor: theme.palette.primary.lightest_shade,
										color: theme.palette.primary.darker_primary,
									},
								})}
								value={index}
							>
								<Typography
									sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
									fontSize={14}
									fontWeight={600}
									lineHeight={'150%'}
								>
									{tab?.code}
								</Typography>
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>

				{tabs.map(({ Component, props }, index) => index == value && <Component key={index} {...props} search={search} />)}
			</Stack>

			{loading && <PageLoader />}
		</Stack>
	);
}
